import { http } from '@pequity/shared/axios/http';
import type { ConfigApiUserRole, Cycle } from '@pequity/shared/ccp/types';
import type { EmployeeResponse } from '@pequity/shared/ccp/types/ccp-details';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, type Ref, toValue } from 'vue';

// Types
export type CycleInfoQueryKeyVars = {
  cycleId: number;
  role: ConfigApiUserRole;
};

// Cycle info fetcher
export const fetchCycleInfo = async (vars: { cycleId: number; role: ConfigApiUserRole }) => {
  const res = await http.get<Cycle>(`v2/ccp/cycles/${vars.cycleId}`, { params: { role: vars.role } });

  return res;
};

// Columns may build from cycle info up to staleTime old, so mutations must invalidate ['cycleInfo'] before ['ccpColsAndFields'].
export const cycleInfoQueryOptions = (vars: MaybeRef<CycleInfoQueryKeyVars>) =>
  queryOptions({
    queryKey: ['cycleInfo', vars],
    queryFn: async () => (await fetchCycleInfo(toValue(vars))).data,
    staleTime: 1500,
  });

// Fetches the cycle info
export const useCycleInfoQuery = (
  vars: Ref<CycleInfoQueryKeyVars>,
  options: { enabled: MaybeRef<boolean> } = { enabled: true }
) => {
  return useQuery({ ...cycleInfoQueryOptions(vars), enabled: options.enabled });
};

// Fetches the employees for settings import to check if the cycle has employees
export const useCcpSettingsEmployeesQuery = (vars: Ref<{ cycleId: number }>) => {
  return useQuery({
    queryKey: ['ccpSettingsEmployees', vars],
    queryFn: async () => {
      const res = await http.post<EmployeeResponse>(
        `v2/ccp/cycles/${vars.value.cycleId}/employees/filtered`,
        {},
        {
          params: { role: 'admin' },
        }
      );

      return res.data;
    },
  });
};

// Ends the cycle
export const useEndCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.put<string>(`v2/ccp/cycles/${vars.cycleId}/end_cycle`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
    },
  });
};

// Archives the cycle
export const useArchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number; revokeAccess: boolean }) =>
      http.put(`v2/ccp/cycles/${vars.cycleId}/archive_cycle`, { revoke_access: vars.revokeAccess }),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Ends and archives the cycle
export const useEndAndArchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { cycleId: number; revokeAccess: boolean }) => {
      await http.put<string>(`v2/ccp/cycles/${vars.cycleId}/end_cycle`);
      await http.put(`v2/ccp/cycles/${vars.cycleId}/archive_cycle`, { revoke_access: vars.revokeAccess });
    },
    onSuccess: async (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      await queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Unarchives the cycle
export const useUnarchiveCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.put<string>(`v2/ccp/cycles/${vars.cycleId}/unarchive_cycle`),
    onSuccess: async (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['ccpSettingsGeneral'] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      await queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};

// Fetches how many cycle employees are not on the HRIS system of record
// and will therefore be excluded from the export
export const useExportToHrisPreviewQuery = (
  vars: Ref<{ cycleId: number }>,
  options: { enabled: MaybeRef<boolean> } = { enabled: true }
) => {
  return useQuery({
    queryKey: ['exportToHrisPreview', vars],
    queryFn: async () => {
      const res = await http.get<{ non_sor_employee_count: number }>(
        `v2/ccp/cycles/${toValue(vars).cycleId}/export-to-hris-preview`
      );

      return res.data;
    },
    enabled: options.enabled,
  });
};

// Exports cycle data to HRIS provider
export const useExportToHrisMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.post(`v2/ccp/cycles/${vars.cycleId}/export-to-hris`),
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
    },
  });
};

// Deletes the cycle (completely removes it)
export const useDeleteCycleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { cycleId: number }) => http.delete(`v2/ccp/cycles/${vars.cycleId}/`),
    onSuccess: async (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['cycleInfo', { cycleId: vars.cycleId }] });
      queryClient.invalidateQueries({ queryKey: ['planningHierarchy', { cycle_id: vars.cycleId }] });
      await queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });
};
